import {type BannerAssessment} from './assessments-banner-card.react-component';
import {CERT_DOMAIN_IDS, SKILL_DOMAIN_IDS} from './topic-icons';

const MAX_BANNER_ASSESSMENTS = 2;

/**
 * Selects up to 2 assessments to display in the banner, ordered by personalization signal strength.
 *
 * Tier 1 — exact topic match: assessment.topics.id intersects user's declared interest IDs.
 * Tier 2 — domain alignment: assessment topicDescriptor IDs match the user's broad interest
 *           category (cert → cloud/cybersecurity, skill → software/ai/ops/business/design). Used to fill
 *           remaining slots when Tier 1 yields fewer than 2 results.
 * Tier 3 — no interest overlap: first 2 assessments from the full collection.
 */
export function selectPersonalizedAssessments(
    all: BannerAssessment[],
    topicInterestIds: string[],
    hasCertInterests: boolean,
    hasSkillInterests: boolean,
): BannerAssessment[] {
    const activeAssessments = all.filter((a) => a.isActive);

    // Tier 1: exact topic match
    const topicIdSet = new Set(topicInterestIds);
    const exactMatches =
        topicIdSet.size > 0
            ? activeAssessments.filter((a) =>
                  a.topics?.some((t) => t != null && topicIdSet.has(t.id)),
              )
            : [];

    if (exactMatches.length >= MAX_BANNER_ASSESSMENTS) {
        return exactMatches.slice(0, MAX_BANNER_ASSESSMENTS);
    }

    // Tier 2: domain alignment — fills remaining slots after exact matches
    const targetDomainIds = new Set([
        ...(hasCertInterests ? Array.from(CERT_DOMAIN_IDS) : []),
        ...(hasSkillInterests ? Array.from(SKILL_DOMAIN_IDS) : []),
    ]);
    const exactMatchIds = new Set(exactMatches.map((a) => a.id));
    const domainMatches =
        targetDomainIds.size > 0
            ? activeAssessments.filter(
                  (a) =>
                      !exactMatchIds.has(a.id) &&
                      a.topicDescriptors?.some((td) => td != null && targetDomainIds.has(td.id)),
              )
            : [];

    const tiered = [...exactMatches, ...domainMatches];
    if (tiered.length > 0) {
        return tiered.slice(0, MAX_BANNER_ASSESSMENTS);
    }

    // Tier 3: no interest overlap — show first 2 from the full (active-only) collection
    return activeAssessments.slice(0, MAX_BANNER_ASSESSMENTS);
}
